/**
 * The two lookups the MF Overview's tables read from.
 *
 * The list endpoints return records that name an investor only by uuid and a
 * scheme only by ISIN. The user directory turns the first into a name, and
 * says whose lead the investor is; the scheme list turns the second into a
 * fund name. Both load once per visit.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeGETRequestOnUserService } from "../../../Api/api";
import { fetchMFSchemes, getMFErrorMessage } from "../../../Api/mfAdminInvestments";
import { isVisibleLead } from "../../../Helpers/adminPermissions";
import type { IUser } from "../../../components/YieldCalculator/Interfaces";

export interface DirectoryInvestor {
  uuid: string;
  name: string;
  email: string;
  mobile: string;
  smestId: string;
  leadOwner: string;
}

export interface InvestorDirectory {
  byUuid: Record<string, DirectoryInvestor>;
  /** Investors this admin may pick — their own leads, or everyone. */
  visible: DirectoryInvestor[];
  loading: boolean;
  error: string;
  reload: () => void;
}

export const useInvestorDirectory = (): InvestorDirectory => {
  const [byUuid, setByUuid] = useState<Record<string, DirectoryInvestor>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const response: any = await makeGETRequestOnUserService("/user/user_list/");
      const users: IUser[] = Array.isArray(response?.data) ? response.data : [];
      const index: Record<string, DirectoryInvestor> = {};
      users.forEach((user) => {
        if (!user.uuid) return;
        index[user.uuid] = {
          uuid: user.uuid,
          name: [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || "SMEST user",
          email: user.emailId || "",
          mobile: user.mobileNumber || "",
          smestId: user.smestID || "",
          leadOwner: user.leadowner || "",
        };
      });
      if (request !== requestRef.current) return;
      setByUuid(index);
    } catch (requestError) {
      if (request !== requestRef.current) return;
      setError(getMFErrorMessage(requestError, "The user directory could not be loaded."));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      requestRef.current += 1;
    };
  }, [load]);

  const visible = useMemo(
    () =>
      Object.keys(byUuid)
        .map((uuid) => byUuid[uuid])
        .filter((investor) => isVisibleLead(investor.leadOwner))
        .sort((left, right) => left.name.localeCompare(right.name)),
    [byUuid]
  );

  return { byUuid, visible, loading, error, reload: load };
};

/** ISIN → fund name. Empty until loaded, and stays empty if the list fails. */
export const useSchemeNames = (): Record<string, string> => {
  const [names, setNames] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    fetchMFSchemes()
      .then((schemes) => {
        if (!active) return;
        const index: Record<string, string> = {};
        schemes.forEach((scheme) => {
          if (scheme.isin && scheme.name) index[scheme.isin] = scheme.name;
        });
        setNames(index);
      })
      // Names are a nicety: without them the tables show the ISIN instead.
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  return names;
};
